/**
 * Notification feed contract, version 1.
 *
 * `GET /api/notifications/feed` returns presentation-ready rows. Web and the
 * native app render the same list from them and keep no copy of these rules.
 *
 * Response:
 *
 *   { version: 1, notifications: FeedResponseRow[], timestamp, hasMore, nextCursor }
 *
 * Each row is the stored notification (extras lifted onto the row, as
 * before) plus a `presentation` object ({@link FeedRowPresentation}). The
 * server has already:
 *
 * - dropped rows that are over (`relevantUntil` passed) or expired;
 * - folded every revision of one job into one row (newest wins) and every
 *   repeat of one notice into one row (`notificationRepeatKey`); the folded
 *   stored ids are in `memberIds`, primary first;
 * - dropped rows the user marked done (durable, all devices) and
 *   completion receipts the user has already seen;
 * - put low-value background rows into day groups (`group`).
 *
 * The server decides, per row: the kind word (`kindLabel`), the priority, the
 * primary action and whether using it finishes the row (`completesRow`),
 * when the row holds NOW or shows under Later (`attention`), the task
 * outcome, group membership, and the durable read/done state (`state`).
 *
 * Clients decide only:
 * - the section, from `now` and the read state, with {@link feedRowSection};
 * - the "when" label, from `when` and the section (`formatWhen`);
 * - pins, which are local to one view;
 * - whether `deviceAction` applies: only the computer whose device id equals
 *   `deviceAction.deviceId` offers it (the web has no device id). Every other
 *   client uses `action`.
 * - rendering. Rows with the same `group.id` render as one expandable row
 *   at the position of the group's first row, labelled `group.label`.
 *
 * Once a verb has opened its screen, a client marks the row read, and also
 * done when the verb says `completesRow` ({@link feedActionCompletesRow}).
 *
 * Writes: `POST /api/notifications/feed` with
 * `{ ops: [{ op: "read" | "done" | "reopen" | "undo", rows: [{ id, revision }] }] }`
 * ({@link FeedStateOp}). `reopen` takes back a Done: the row is back as it was before it;
 * `undo` forgets the row's state (unread, not done). `id` and `revision` are the row's
 * `presentation.id` and `presentation.revision`. State applies only while the
 * revision matches, so a row whose content changes comes back unread. A group
 * row is not an entity: send one entry per member row. The same route still
 * takes a continuity hand-off acknowledgement (`{ handoff, expectedRevision }`).
 *
 * Changes after version 1 are additive only. A breaking change bumps
 * {@link NOTIFICATION_FEED_CONTRACT_VERSION}.
 */

import { readActionApprovalCard } from "@/src/features/jobs/actionApprovalNotification";
import { completionAskKey, parseCompletionAwaiting, type CompletionAwaiting } from "@/src/features/jobs/completionAwaiting";
import { COMPLETION_NOTIFICATION_FALLBACK_SUMMARY } from "@/src/features/jobs/completionCompute";
import { completionNotification } from "@/src/features/jobs/completionNotification";
import type { NotificationHandoffContract } from "@/src/features/jobs/continuity/contracts";
import { readIntegrationReviewCard } from "@/src/features/integrations/integrationReviewCard";
import { parseDateKey } from "@/src/features/calendar/core/userClock";
import {
  calendarInsightNamedDay,
  calendarInsightRelevantUntil,
  namedDayZone,
  type NamedDay,
} from "@/src/features/notifications/calendarInsightRows";
import { addContactsLabel, readContactSuggestionCard } from "@/src/features/notifications/contactSuggestionCard";
import {
  DRAFT_FOLLOW_UP_LABEL,
  readDeadlineReminderCard,
  readFollowUpDraftCard,
  SET_REMINDER_LABEL,
} from "@/src/features/notifications/insightActionCards";
import { notificationBatteryFeedOf } from "@/src/features/notifications/notificationBatteryFeed";
import { DEVICE_SPACE_ACTION_LABEL, readDeviceSpaceOffer, type DeviceSpaceFocus } from "@/src/features/notifications/deviceSpace";
import { getNotificationRelevantUntil, type LifecycleNotification } from "@/src/features/notifications/lifecycle";
import {
  getNotificationPriority,
  PRIORITY_LABEL,
  PRIORITY_RANK,
  type NotificationPriority,
  type PrioritizableNotification,
} from "@/src/features/notifications/priorityTier";
import {
  getNotificationAnchor,
  isTimeAnchoredNotification,
  MOMENT_PASSED_MS,
  needsUserAction,
  NEXT_GRACE_MS,
  notificationKinds,
  notificationTime,
} from "@/src/features/notifications/tiers";
import { trustedCommunicationActionUrl } from "@/src/features/platform-communications/actionUrl";
import { normalizePlainTextForDisplay } from "@/src/shared/text/normalizePlainTextForDisplay";
import { leadingSentences } from "@smartmanager/shell-ui/shared/text/sentences";
import { sourceEnglish, type Translate } from "@smartmanager/shell-ui/shared/i18n/translate";
import { opensSocialConversations } from "@/src/features/social-management/client/openTab";

export const NOTIFICATION_FEED_CONTRACT_VERSION = 1 as const;

// ── Row facts ───────────────────────────────────────────────────────────────

/** What kind of thing a row is. `kindLabel` is its word for the meta line. */
export type FeedKind =
  | "approval"
  | "scam_warning"
  | "conflict"
  | "deadline"
  | "planning"
  | "meeting_prep"
  | "reminder"
  | "connection"
  | "email"
  | "message"
  | "follow_up"
  | "suggestion"
  | "confirmed_meeting"
  | "weather"
  | "device"
  | "sharing"
  | "platform"
  | "task"
  | "question"
  | "failed_task"
  | "cancelled_task"
  | "calendar"
  | "update";

export const FEED_KIND_LABEL: Record<FeedKind, string> = {
  approval: "Approval",
  scam_warning: "Scam warning",
  conflict: "Calendar conflict",
  deadline: "Deadline",
  planning: "Planning",
  meeting_prep: "Meeting prep",
  reminder: "Reminder",
  connection: "Connection",
  email: "Email",
  message: "Message",
  follow_up: "Follow-up",
  suggestion: "Suggestion",
  confirmed_meeting: "Confirmed meeting",
  weather: "Weather",
  device: "Device",
  sharing: "Sharing",
  platform: "From Smart Manager",
  task: "Task",
  question: "Question",
  failed_task: "Failed task",
  cancelled_task: "Cancelled task",
  calendar: "Calendar",
  update: "Update",
};

/** How a task ended. Null on rows that are not about a task. */
export type FeedOutcome = "succeeded" | "failed" | "cancelled";

export type FeedPriority = NotificationPriority;

/**
 * Whether using a verb finishes its row. True when the screen the verb
 * opens takes the task over and keeps it in view until it is done (the
 * space screen, the Conversations tab), so the row has nothing left to say.
 * Absent or false when the screen only shows the thing the row is about,
 * or when the row is the only reminder of a problem that stays until it is
 * fixed: opening such a verb only reads the row.
 */
export type FeedActionCompletion = { completesRow?: boolean };

/**
 * What the row's verb does. Plain data: every client maps a type to its own
 * screen. `label` is the verb the row shows.
 */
export type FeedAction = FeedActionCompletion & (
  | { type: "job_completion"; label: string; target: { handoff: NotificationHandoffContract; sceneId: string | null } }
  | { type: "platform_access"; label: string }
  | { type: "support_ticket"; label: string; ticketId: string }
  | { type: "settings_sync"; label: string; vendor?: string }
  | { type: "document"; label: string; documentId: string }
  | { type: "calendar_item"; label: string; calendarItemId: string; preferredView?: "month" | "week" | "workweek" }
  | { type: "messages"; label: string; platform?: string; conversationId?: string; showUnreadOnly?: boolean }
  | { type: "external_url"; label: string; url: string }
  /** Opens the social studio on Conversations (a daily scan found threads). */
  | { type: "social_conversations"; label: string }
  /**
   * Adds a contact suggestion's people to the user's Smart Manager contacts
   * (never a provider's address book). The server reads them from the row.
   */
  | { type: "add_contacts"; label: string; count: number }
  /**
   * Sets the reminder a deadline alert offers: a Smart Manager reminder in
   * the user's silo (never a provider's calendar). The server reads the
   * deadline from the row; Undo takes the reminder back.
   */
  | { type: "set_reminder"; label: string }
  /**
   * Drafts the follow-up a follow-up nudge offers and opens it for review.
   * Nothing is sent: the draft's own Send is the person's approval.
   */
  | { type: "draft_follow_up"; label: string }
);

/** "Make more space", offered only on the computer the alert is about. */
export type FeedDeviceAction = {
  /** The device id the desktop app reported. A client offers this only when it is its own id. */
  deviceId: string;
  action: FeedActionCompletion & { type: "open_device_space"; label: string; focus: DeviceSpaceFocus };
};

/** True when using `action` finishes its row: the client marks the row done, not just read. */
export function feedActionCompletesRow(action: FeedActionCompletion | null | undefined): boolean {
  return action?.completesRow === true;
}

/**
 * A span of time in which the row shows in a section. `from`/`until` are ms
 * epoch, `null` means open-ended; the span is `from <= now < until`. A
 * window with `releasedByRead` ends once the user has seen the row.
 */
export type FeedWindow = {
  section: "now" | "later";
  from: number | null;
  until: number | null;
  releasedByRead: boolean;
};

/** Durable per-user state, the same on every device. */
export type FeedRowState = { read: boolean; done: boolean };

/**
 * Rows rendered as one expandable row: a day group of low-value background
 * rows, or the unseen task results in NOW.
 */
export type FeedGroup = {
  /**
   * Shared by every member. A day group's is stable for a kind, outcome and
   * local day (`g:<kind>:<day>`); the unseen results' is {@link UNSEEN_RESULTS_GROUP_ID}.
   */
  id: string;
  /** "3 task results". */
  label: string;
  count: number;
  /** The members' `presentation.id`, newest first. */
  memberRowIds: string[];
};

/** What the "when" column is computed from (`formatWhen`). */
export type FeedWhen = {
  /** Start of the thing the row is about (ms epoch), or null. */
  anchorAt: number | null;
  /** When the row was stored (ms epoch). */
  timestamp: number;
  isDeadline: boolean;
  /** A deadline's last moment, when known: due, not overdue, until then. */
  dueUntil: number | null;
  /**
   * The day the row's copy names and the zone that named it, so the column
   * says the same day as the text beside it. Null when the row names no day
   * the reader can trust; absent on rows materialized before it.
   */
  namedDay?: NamedDay | null;
};

export type FeedRowPresentation = {
  version: typeof NOTIFICATION_FEED_CONTRACT_VERSION;
  /** Stable row id: one per job, per repeated notice, or per stored row. Ops target it. */
  id: string;
  /** Stored notification ids this row stands for, primary (newest) first. */
  memberIds: string[];
  /** Content revision. Read and done apply only while it matches. */
  revision: string;
  kind: FeedKind;
  kindLabel: string;
  priority: FeedPriority;
  /** "Critical", "High", or null for normal. */
  priorityLabel: string | null;
  outcome: FeedOutcome | null;
  /** The line to show. */
  summary: string;
  /** Null when the row has no verb, or answers through its own card (approvals, integration reviews). */
  action: FeedAction | null;
  deviceAction: FeedDeviceAction | null;
  /**
   * The row waits on the person and says so: it holds NOW until it is
   * answered ({@link feedRowWaitsOnPerson}), and the surfaces showing it can
   * answer it. False when only a device action answers it: the computer it
   * is about offers that verb, and every other surface shows the row as a
   * fact, not as a demand it cannot act on. The single source: the response
   * row's top-level `actionRequired` carries the same value
   * (`shapeNotificationFeed`), and no client re-derives it from stored fields.
   */
  actionRequired: boolean;
  /**
   * A new copy of this row may play the alert-tier sound: its producer marked
   * it as needing the person (the stored claim, on any computer, so a disk
   * alert chimes where only its own computer can answer it), or it is a
   * question. Platform messages never chime: a broadcast reaches every open
   * window. Wider than the stored claim only by questions; reply-needed
   * messages and access requests hold NOW without chiming.
   */
  attentionCue: boolean;
  /** In order: the first window that holds `now` and is not released decides the section. */
  attention: FeedWindow[];
  /** When the row stops showing at all (ms epoch), or null when only the user can end it. */
  relevantUntil: number | null;
  when: FeedWhen;
  state: FeedRowState;
  group: FeedGroup | null;
};

export type FeedSection = "now" | "later" | "background";

// ── Client helpers (pure, time-dependent) ───────────────────────────────────

const inWindow = (window: FeedWindow, now: number) =>
  (window.from === null || window.from <= now) && (window.until === null || now < window.until);

/**
 * The row's section at `now`, or null when it is not shown. `read` and
 * `done` are the server state merged with the client's optimistic state.
 * A pinned row stays in NOW while the view holds it, even when over.
 */
export function feedRowSection(
  presentation: Pick<FeedRowPresentation, "attention" | "relevantUntil">,
  { now, read, done, pinned = false }: { now: number; read: boolean; done: boolean; pinned?: boolean },
): FeedSection | null {
  if (pinned) return "now";
  if (done) return null;
  if (presentation.relevantUntil !== null && presentation.relevantUntil <= now) return null;
  const window = presentation.attention.find((candidate) => inWindow(candidate, now) && !(candidate.releasedByRead && read));
  return window ? window.section : "background";
}

/**
 * Order inside a section.
 * - NOW: priority first. Within a priority, rows tied to a moment (a meeting,
 *   a deadline) come first, soonest moment on top. The other rows follow,
 *   newest first, so the notice that just arrived is on top.
 * - Later is a schedule: priority, then soonest moment first.
 * - Background: newest first.
 */
export function compareFeedRows(section: FeedSection, a: FeedRowPresentation, b: FeedRowPresentation): number {
  if (section === "background") return b.when.timestamp - a.when.timestamp;
  const byPriority = PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority];
  if (byPriority !== 0) return byPriority;
  const aAnchor = a.when.anchorAt;
  const bAnchor = b.when.anchorAt;
  if (section === "now") {
    if (aAnchor !== null && bAnchor !== null) return aAnchor - bAnchor;
    if (aAnchor !== null || bAnchor !== null) return aAnchor !== null ? -1 : 1;
    return b.when.timestamp - a.when.timestamp;
  }
  return (aAnchor ?? a.when.timestamp) - (bAnchor ?? b.when.timestamp);
}

/**
 * The verb this client offers: the device action on the computer it is
 * about (`thisDeviceId` is the desktop app's id, null on the web), else
 * `action`.
 */
export function feedRowAction(
  presentation: Pick<FeedRowPresentation, "action" | "deviceAction">,
  thisDeviceId: string | null,
): FeedAction | FeedDeviceAction["action"] | null {
  const device = presentation.deviceAction;
  if (device && thisDeviceId && device.deviceId === thisDeviceId) return device.action;
  return presentation.action;
}

/**
 * A NOW window only an answer ends: open from the start, not released by a
 * read. Only a row that waits on the person carries one (`attentionWindows`
 * for `needsUserAction`, and a completion that asks the person something).
 */
export const isAnswerHold = (window: FeedWindow): boolean =>
  window.section === "now" && window.from === null && !window.releasedByRead;

/**
 * The person must act on this row somewhere, on this surface or on the
 * computer a device action names. `actionRequired` narrows it to rows the
 * surfaces showing it can answer.
 */
export function feedRowWaitsOnPerson(presentation: Pick<FeedRowPresentation, "attention">): boolean {
  return presentation.attention.some(isAnswerHold);
}

/** True when `value` is a v1 presentation a client can render (the server sent it). */
export function isFeedRowPresentation(value: unknown): value is FeedRowPresentation {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const presentation = value as Record<string, unknown>;
  return presentation.version === NOTIFICATION_FEED_CONTRACT_VERSION
    && typeof presentation.id === "string" && presentation.id.length > 0
    && typeof presentation.revision === "string"
    && Array.isArray(presentation.memberIds) && Array.isArray(presentation.attention);
}

/** A client's key for its optimistic read or done of a row at one revision. */
export function feedRowStateKey(presentation: Pick<FeedRowPresentation, "id" | "revision">): string {
  return `${presentation.id}#${presentation.revision}`;
}

// ── Server: per-row presentation ────────────────────────────────────────────

/**
 * A notification as the presentation reads it: stored columns on top and
 * stored extras nested under `extraFields` (`NotificationFeedItem`).
 */
export type PresentableNotification = LifecycleNotification & PrioritizableNotification & {
  id?: unknown;
  _key: string;
  _timestamp?: unknown;
  summary?: unknown;
  vendor?: unknown;
  sourceObjectId?: unknown;
  dedupeKey?: unknown;
  storedDate?: unknown;
  /** The producer's revision basis, lifted from stored extras ({@link feedRevisionKey}). */
  feedRevisionKey?: unknown;
};

/** A successful completion row holds NOW, unseen, at most this long after it was stored. */
export const COMPLETION_HOLD_MS = 24 * 60 * 60 * 1000;

/**
 * When a completion holds NOW. Only an unseen hand-off holds it, and a read
 * releases it.
 * - A success holds NOW for {@link COMPLETION_HOLD_MS}.
 * - A failure holds NOW until it is seen: the person has to learn that it
 *   did not happen.
 * - A cancelled task never holds NOW: the person stopped it, or a newer
 *   request replaced it.
 */
export function completionAttention(handoffStatus: string, outcome: FeedOutcome | null, storedAt: number): FeedWindow[] {
  if (handoffStatus !== "pending" || outcome === "cancelled") return [];
  return [{ section: "now", from: null, until: outcome === "failed" ? null : storedAt + COMPLETION_HOLD_MS, releasedByRead: true }];
}

/**
 * When a question holds NOW (`completionQuestion`). It waits on the person
 * until they open it (the hand-off leaves pending) or mark it Done, for at
 * most {@link COMPLETION_HOLD_MS}. Reading the row does not answer it, so a
 * read does not release it.
 */
export function questionAttention(handoffStatus: string, storedAt: number): FeedWindow[] {
  if (handoffStatus !== "pending") return [];
  return [{ section: "now", from: null, until: storedAt + COMPLETION_HOLD_MS, releasedByRead: false }];
}

/** The verb of a question row: it opens the conversation where the person answers. */
export const QUESTION_ACTION_LABEL = "Answer";

const COMPLETION_SUMMARY = "Task update ready to review.";
const CANCELLED_SUMMARY = "Task cancelled.";

const text = (value: unknown) => (typeof value === "string" ? value.trim() : "");
const lower = (value: unknown) => text(value).toLowerCase();
const record = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : null;

/**
 * The day a row's copy names, as its producer stored it: `dayKey` with the
 * zone the user set that named it (`dayKeyZone`), or a deadline's all-day
 * date. A day without its zone (a conflict notice, a deadline named in the
 * UTC fallback) is not one: the row reads its anchor in the viewer's zone.
 */
function namedDay(item: PresentableNotification): NamedDay | null {
  const fields = { ...record(item.extraFields), ...record(item) };
  const key = text(fields.dayKey);
  const timeZone = namedDayZone(fields.dayKeyZone);
  return parseDateKey(key) && timeZone ? { key, timeZone } : calendarInsightNamedDay(item);
}

const MESSAGE_CATEGORIES = ["slack", "teams", "discord", "telegram", "whatsapp", "imessage", "message", "messaging"];

/** The kind of a row, before its task outcome. */
function baseKind(item: PresentableNotification): FeedKind {
  const kinds = notificationKinds(item);
  const objectType = lower(item.sourceObjectType);
  const any = (...candidates: string[]) => candidates.some((kind) => kinds.has(kind));

  if (any("action_approval")) return "approval";
  // Before the email kinds: a network phishing warning about an email.
  if (any("email_threat")) return "scam_warning";
  if (any("conflict_alert")) return "conflict";
  if (any("deadline_alert")) return "deadline";
  if (any("overload_suggestion")) return "planning";
  if (any("meeting_brief") || record(item.extraFields)?.bundleType === "pre_meeting_context") return "meeting_prep";
  if (any("reminder", "derived_prep", "proactive_reminder", "calendar_derived_reminders")) return "reminder";
  if (any("integration_alert", "reauthorize_required", "integration_review") || objectType === "integration") return "connection";
  if (any("message_attention", "reply_attention") || objectType === "message" || any(...MESSAGE_CATEGORIES, "email")) {
    return kinds.has("email") ? "email" : "message";
  }
  if (any("follow_up_tracker", "obligation_follow_up", "counterparty_commitment")) return "follow_up";
  if (any("networking_suggestion")) return "suggestion";
  if (any("meeting_confirmed")) return "confirmed_meeting";
  if (any("weather_alert")) return "weather";
  if (any("device_health")) return "device";
  if (objectType === "platform_access_request" || objectType === "shared_activity" || any("shared_activity")) return "sharing";
  if (any("platform_message", "platform_communication")) return "platform";
  if (any("task_update") || objectType === "job" || objectType === "todo") return "task";
  if (any("calendar") || objectType === "event") return "calendar";
  return "update";
}

/** The kind of a row; a task that did not succeed says so. */
export function feedKind(item: PresentableNotification, outcome: FeedOutcome | null = null): FeedKind {
  const kind = baseKind(item);
  if (kind !== "task") return kind;
  if (outcome === "failed") return "failed_task";
  if (outcome === "cancelled") return "cancelled_task";
  return kind;
}

/**
 * The outcome a completion row stored at publish (`extraFields.jobOutcome`),
 * or null when the row is not a completion or predates the field.
 */
export function storedCompletionOutcome(item: { extraFields?: unknown }): CompletionFacts | null {
  const stored = record(record(item.extraFields)?.jobOutcome);
  if (!stored) return null;
  const outcome = jobStatusOutcome(stored.status);
  if (!outcome) return null;
  const count = stored.componentCount;
  return {
    outcome,
    componentCount: typeof count === "number" && Number.isSafeInteger(count) && count >= 0 ? count : null,
    awaiting: outcome === "succeeded" ? parseCompletionAwaiting(stored.awaiting) : null,
  };
}

/**
 * What a completion asks the person for, or null when it is a result. A job
 * that finished by asking (`CompletionFacts.awaiting`) is a question: its row
 * is the `question` kind, holds NOW until it is opened or done
 * ({@link questionAttention}), never joins the task results, and pending
 * copies of one question fold into one row ({@link completionQuestionKey}).
 */
export function completionQuestion(completion: CompletionFacts | null): CompletionAwaiting | null {
  return completion?.outcome === "succeeded" ? completion.awaiting ?? null : null;
}

/** The fold key (`ask:...`) of a completion that is a question with a known subject, or null. */
export function completionQuestionKey(completion: CompletionFacts | null): string | null {
  const question = completionQuestion(completion);
  return question ? completionAskKey(question) : null;
}

/** A job status (`completed`, `failed`, `cancelled`, ...) as an outcome, or null while it runs. */
export function jobStatusOutcome(status: unknown): FeedOutcome | null {
  const value = lower(status);
  if (value === "completed" || value === "succeeded") return "succeeded";
  if (value === "failed" || value === "error") return "failed";
  if (value === "cancelled" || value === "canceled") return "cancelled";
  return null;
}

/**
 * The row's primary action for any client, and the device-only action that
 * replaces it on the computer an alert is about. Approvals and integration
 * reviews answer through their own card and get none. `componentCount` is a
 * completion's ({@link CompletionFacts}); a cancelled task needs one above 0.
 */
export function feedPrimaryAction(
  item: PresentableNotification,
  outcome: FeedOutcome | null = null,
  componentCount: number | null = null,
): { action: FeedAction | null; deviceAction: FeedDeviceAction | null } {
  if (readActionApprovalCard(item) || readIntegrationReviewCard(item)) return { action: null, deviceAction: null };
  const offer = readDeviceSpaceOffer(item);
  const deviceAction: FeedDeviceAction | null = offer?.reportedDeviceId
    // The space screen measures what can go and asks before it clears
    // anything: it owns the problem from here, and the alert returns only if
    // the problem gets worse (`feedRevisionKey` in deviceHealthEpisodes).
    ? { deviceId: offer.reportedDeviceId, action: { type: "open_device_space", label: DEVICE_SPACE_ACTION_LABEL, focus: offer.focus, completesRow: true } }
    : null;
  return { action: defaultAction(item, outcome, componentCount), deviceAction };
}

function defaultAction(item: PresentableNotification, outcome: FeedOutcome | null, componentCount: number | null): FeedAction | null {
  const completion = completionNotification(item);
  if (completion) {
    // A cancelled task opens only to a partial result it left: the components
    // it added before it stopped. Without them there is nothing to open.
    if (outcome === "cancelled" && !(componentCount !== null && componentCount > 0)) return null;
    // No retry path exists for a finished job; a failed one opens to show what happened.
    const label = outcome === "failed" || outcome === "cancelled" ? "Open" : "Open result";
    return { type: "job_completion", label, target: { handoff: completion.handoff, sceneId: completion.sceneId } };
  }
  // A contact suggestion's verb does the work: it adds the people it found.
  const contactSuggestion = readContactSuggestionCard(item);
  if (contactSuggestion) {
    const count = contactSuggestion.people.length;
    return { type: "add_contacts", label: addContactsLabel(count), count };
  }
  // A deadline alert's verb sets the reminder it offers; a follow-up nudge's
  // drafts the follow-up (`insightActionCards`).
  if (readDeadlineReminderCard(item)) return { type: "set_reminder", label: SET_REMINDER_LABEL };
  if (readFollowUpDraftCard(item)) return { type: "draft_follow_up", label: DRAFT_FOLLOW_UP_LABEL };
  const extraFields = record(item.extraFields);
  const sourceObjectType = lower(item.sourceObjectType);
  const notificationType = lower(item.notificationType);
  const source = lower(item.source);
  if (sourceObjectType === "shared_activity" || sourceObjectType === "platform_access_request") {
    return { type: "platform_access", label: "Review" };
  }
  const sourceObjectId = text(typeof item.sourceObjectId === "string" || typeof item.sourceObjectId === "number" ? String(item.sourceObjectId) : "");
  // Only the owner alert opens the inbox: replies to the person who asked
  // also point at a support ticket and must not.
  if (notificationType === "support_ticket_alert" || extraFields?.componentName === "SupportInbox") {
    const ticketId = text(record(extraFields?.componentProps)?.ticketId) || sourceObjectId;
    return { type: "support_ticket", label: "Open", ticketId };
  }
  // The replies wait for the OK in the Conversations tab, and tomorrow's run
  // raises a new notice if they still wait.
  if (opensSocialConversations(extraFields)) return { type: "social_conversations", label: "Open", completesRow: true };
  const platformAction = record(record(extraFields?.platformCommunication)?.action);
  const platformActionUrl = trustedCommunicationActionUrl(platformAction?.url);
  const platformActionLabel = text(platformAction?.label);
  if (source === "platform_communication" && platformActionUrl && platformActionLabel) {
    return { type: "external_url", label: platformActionLabel, url: platformActionUrl };
  }
  const meetingReviewProvider = text(record(extraFields?.integrationReview)?.provider);
  if (notificationType === "integration_review" && meetingReviewProvider) {
    return { type: "settings_sync", label: "Review", vendor: `meeting-catalog:${meetingReviewProvider}` };
  }
  const documentId = text(extraFields?.topDocumentId) || (sourceObjectType === "document" ? sourceObjectId : "");
  if (documentId) return { type: "document", label: "Open file", documentId };
  if (sourceObjectType === "integration" || notificationType === "integration_alert" || source === "reauthorize_required") {
    return { type: "settings_sync", label: "Reconnect", vendor: sourceObjectId || text(item.vendor) };
  }
  if (sourceObjectType === "message" || source === "message_attention" || extraFields?.componentName === "UnifiedMessaging") {
    const platform = text(extraFields?.platform) || text(item.vendor);
    const conversationId = text(extraFields?.conversationId) || sourceObjectId;
    return {
      type: "messages",
      label: "Reply",
      ...(platform ? { platform } : {}),
      ...(conversationId ? { conversationId } : {}),
      showUnreadOnly: extraFields?.showUnreadOnly !== false,
    };
  }
  // Any other insight about one email names the email, and no client opens a
  // single email or thread, so the row has no verb (tap opens its detail). It
  // must not borrow a calendar verb from stray ids in its payload.
  if (sourceObjectType === "email") return null;
  const firstEvent = Array.isArray(extraFields?.events) ? record(extraFields.events[0]) : null;
  const calendarItemId = sourceObjectType === "event" || sourceObjectType === "todo"
    ? sourceObjectId
    : text(extraFields?.meetingId) || text(extraFields?.eventId) || text(firstEvent?.id);
  if (sourceObjectType === "todo" && calendarItemId) {
    return { type: "calendar_item", label: "Open task", calendarItemId, preferredView: "week" };
  }
  if (calendarItemId) {
    return { type: "calendar_item", label: text(extraFields?.meetingId) ? "Open meeting" : "Open calendar", calendarItemId, preferredView: "week" };
  }
  return null;
}

// Kinds that hold NOW until seen (tiers.ts NOW_TYPES).
const ATTENTION_TYPES = new Set([
  "conflict_alert",
  "deadline_alert",
  "reply_attention",
  "integration_alert",
  "reauthorize_required",
  "integration_review",
  "overload_suggestion",
  "obligation_follow_up",
  "weather_alert",
  // The week's "Done for you" digest: what standing mandates did on their
  // own, seen once each time a new run (or one that did not go through) lands.
  "mandate_digest",
]);

// Receipts: never promoted to Later by an incidental start time (tiers.ts LOG_TYPES).
const RECEIPT_TYPES = new Set([
  "digest",
  "sync_summary",
  "meeting_confirmed",
  "task_update",
  "shared_activity",
  "networking_suggestion",
  "follow_up_tracker",
]);

/**
 * When a non-completion row holds NOW or shows under Later: the
 * `classifyNotificationTier` rules, expressed as time windows so a client
 * can place the row at any `now` without the rules.
 */
export function attentionWindows(item: PresentableNotification): FeedWindow[] {
  const anchorAt = getNotificationAnchor(item);
  const kinds = Array.from(notificationKinds(item));
  const has = (set: Set<string>) => kinds.some((kind) => set.has(kind));
  const urgency = typeof item.urgency === "number" ? item.urgency : Number(item.urgency ?? 2) || 2;
  const timeAnchored = isTimeAnchoredNotification(item);
  // The moment has passed once now > anchor + MOMENT_PASSED_MS.
  const momentEnd = anchorAt === null ? null : anchorAt + MOMENT_PASSED_MS + 1;
  // A time-anchored row is history once its moment has passed, whatever else holds it.
  const cutoff = timeAnchored ? momentEnd : null;
  const windows: FeedWindow[] = [];

  if (needsUserAction(item)) windows.push({ section: "now", from: null, until: cutoff, releasedByRead: false });
  const isAttentionKind = has(ATTENTION_TYPES) || (kinds.includes("device_health") && urgency >= 3);
  if (isAttentionKind) windows.push({ section: "now", from: null, until: cutoff, releasedByRead: true });

  if (anchorAt !== null) {
    if (timeAnchored) {
      // Coming up, then NOW from its start through the grace window.
      windows.push({ section: "later", from: null, until: anchorAt, releasedByRead: false });
      windows.push({ section: "now", from: anchorAt, until: Math.min(anchorAt + NEXT_GRACE_MS + 1, momentEnd ?? Infinity), releasedByRead: false });
    } else if (!has(RECEIPT_TYPES)) {
      windows.push({ section: "later", from: null, until: anchorAt, releasedByRead: false });
    }
  }
  // An unseen alert holds NOW until its moment has passed.
  if (urgency >= 4 || item.presentationMode === "moment_alert") {
    windows.push({ section: "now", from: null, until: momentEnd, releasedByRead: true });
  }
  return withoutShadowedWindows(windows);
}

/**
 * `windows` without the ones that can never decide a section. A window is
 * shadowed when an earlier one spans all of its time and holds whenever it
 * does: the earlier one is never released by a read, or both are. The first
 * holding window wins, so dropping a shadowed one changes no section; it only
 * keeps the contract from repeating "now" several times.
 */
export function withoutShadowedWindows(windows: readonly FeedWindow[]): FeedWindow[] {
  const covers = (outer: FeedWindow, inner: FeedWindow) =>
    (outer.from === null || (inner.from !== null && outer.from <= inner.from))
    && (outer.until === null || (inner.until !== null && inner.until <= outer.until))
    && (!outer.releasedByRead || inner.releasedByRead);
  const kept: FeedWindow[] = [];
  for (const window of windows) {
    if (!kept.some((earlier) => covers(earlier, window))) kept.push(window);
  }
  return kept;
}

/** Small stable content hash (FNV-1a, 32 bit, base36). Not a security boundary. */
export function contentHash(value: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(36);
}

/** What a completion row needs beyond the row itself. */
export type CompletionFacts = {
  outcome: FeedOutcome;
  /** Components the job added to the turn; 0 means the answer is the whole result. Null when unknown. */
  componentCount: number | null;
  /** What a completed job asks the person for ({@link completionQuestion}). Null or absent: nothing. */
  awaiting?: CompletionAwaiting | null;
};

export type RowPresentationInput = {
  item: PresentableNotification;
  /**
   * The fold key (`job:<id>`, `subject:<repeat key>`, `dup:<copy key>`,
   * `ask:<question key>`), or null for a row of its own.
   */
  foldKey: string | null;
  /**
   * A subject fold's occurrence: its oldest member's stored id
   * (`prepareNotificationRows`, `shapeNotificationFeed`). A question fold's
   * occurrence: where it starts (`questionRowId`). Ignored for other rows.
   */
  occurrence?: string;
  memberIds: string[];
  completion: CompletionFacts | null;
  state: FeedRowState;
};

/** The presentation of one (already folded) row. `group` is filled in later by {@link groupFeedRows}. */
export function buildRowPresentation({ item, foldKey, occurrence: subjectOccurrence, memberIds, completion, state }: RowPresentationInput): FeedRowPresentation {
  const handoff = completionNotification(item)?.handoff ?? null;
  const outcome = completion?.outcome ?? (handoff ? "succeeded" : null);
  const question = handoff !== null && completionQuestion(completion) !== null;
  const kind: FeedKind = question ? "question" : feedKind(item, outcome);
  const priority = getNotificationPriority(item);
  const primary = feedPrimaryAction(item, outcome, completion?.componentCount ?? null);
  // A question is answered in its conversation, and its verb says so.
  const action = question && primary.action?.type === "job_completion" ? { ...primary.action, label: QUESTION_ACTION_LABEL } : primary.action;
  const deviceAction = primary.deviceAction;
  const storedAt = notificationTime(item._timestamp) ?? notificationTime(item.storedDate) ?? 0;
  const summary = presentedSummary(item, handoff !== null, outcome);
  const attention = !handoff ? attentionWindows(item)
    : question ? questionAttention(handoff.status, storedAt) : completionAttention(handoff.status, outcome, storedAt);
  const relevantUntil = minOf(getNotificationRelevantUntil(item), calendarInsightRelevantUntil(item));
  const anchorAt = getNotificationAnchor(item);
  // The occurrence: for a row of its own, its stored id (an alert raised again
  // as a new row is new even when it reads the same, so it comes back after
  // Done); for a job, the winning stored id (one per result: a newer result of
  // the same job returns). A subject fold stands for copies producers may
  // store under a new id while the subject is still in the feed: its oldest
  // member is the occurrence, so a coexisting copy (whichever wins) keeps Done,
  // and once every copy has left the feed the next one is news again
  // (`repeatNotices.ts`). A copy fold (`dup:`) carries the notice's anchor in
  // its key, so the key is the occurrence. `createdDate` is not used: a write
  // that does not set it stamps the write time, so every producer run would
  // undo Done. A question fold (`ask:`) is one occurrence of the question:
  // where it starts (the time the previous one was Done, or "start"), so a
  // copy leaving the feed or another page never changes it (`questionRowId`).
  const occurrence = foldKey === null || foldKey.startsWith("job:")
    ? item._key
    : foldKey.startsWith("subject:") ? `subject#${subjectOccurrence ?? item._key}`
      : foldKey.startsWith("ask:") ? `ask#${subjectOccurrence ?? QUESTION_FIRST_OCCURRENCE}` : foldKey;
  //
  // Only what the row itself stores goes in: a completion row without a stored
  // outcome hashes the publish default ("succeeded"), so a job status read
  // later (or not readable at all) changes how the row reads, not whether
  // read and done still hold.
  const contentOutcome = handoff ? storedCompletionOutcome(item)?.outcome ?? "succeeded" : outcome;
  // A question fold's copies word one question differently (each answer is
  // generated), so its revision is the question, not the newest copy's
  // words: a copy arriving or leaving keeps read and Done.
  //
  // A row its producer rewrites in place from live readings (a device alert)
  // names its own revision basis (`feedRevisionKey`): the numbers in its
  // words drift with every reading, and only the producer knows which change
  // is news. Read and Done hold while that key stays the same.
  const producerKey = feedRevisionKey(item);
  const revision = foldKey?.startsWith("ask:")
    ? contentHash(JSON.stringify([foldKey, priority, occurrence]))
    : producerKey !== null ? contentHash(JSON.stringify(["key", producerKey, occurrence]))
    : contentHash(JSON.stringify([
      presentedSummary(item, handoff !== null, contentOutcome), feedKind(item, contentOutcome), contentOutcome,
      priority, item.actionRequired === true, anchorAt, occurrence,
    ]));
  return {
    version: NOTIFICATION_FEED_CONTRACT_VERSION,
    // A question fold's id names its occurrence too: each time the question
    // is asked anew it is a row of its own, so its read and Done never
    // overwrite the Done of an earlier time it was asked (`questionRowId`).
    id: foldKey?.startsWith("ask:") ? questionRowId(foldKey, subjectOccurrence ?? QUESTION_FIRST_OCCURRENCE) : rowId(foldKey, item._key),
    memberIds,
    revision,
    kind,
    kindLabel: FEED_KIND_LABEL[kind],
    priority,
    priorityLabel: PRIORITY_LABEL[priority],
    outcome,
    summary,
    action,
    deviceAction,
    // One rule with the hold: a row says it waits on the person exactly when
    // it holds NOW until answered, unless only a device action answers it.
    actionRequired: attention.some(isAnswerHold) && !(action === null && deviceAction !== null),
    attentionCue: kind !== "platform" && (item.actionRequired === true || question),
    attention,
    relevantUntil,
    when: { anchorAt, timestamp: storedAt, isDeadline: notificationKinds(item).has("deadline_alert"), dueUntil: calendarInsightRelevantUntil(item), namedDay: namedDay(item) },
    state,
    group: null,
  };
}

/**
 * A battery-runway alert's revision basis. The row is rewritten with the time
 * left on every sample, and a new alert is a new row
 * (`batteryRunwayAlertRowId`), so nothing said on one row is news after its
 * first reading. Its fixed-shape silo write (`batteryRunwayFeed` sync) takes
 * no extra field, so the key is named here, from the row's battery role.
 */
const BATTERY_RUNWAY_REVISION_KEY = "battery-runway";

/**
 * The producer's revision basis, stored as `feedRevisionKey` (feed rows lift
 * stored extras onto the row; rows built for display keep them under
 * `extraFields`), or null when the producer named none.
 */
export function feedRevisionKey(item: object): string | null {
  const row = item as Record<string, unknown>;
  const key = text(row.feedRevisionKey) || text(record(row.extraFields)?.feedRevisionKey);
  if (key) return key;
  return notificationBatteryFeedOf(row)?.role === "alert" ? BATTERY_RUNWAY_REVISION_KEY : null;
}

/** Stable, bounded row id. Fold keys can carry notice text, so they are hashed. */
export function rowId(foldKey: string | null, storedId: string): string {
  if (foldKey === null) return `n:${storedId}`;
  if (foldKey.startsWith("job:")) return foldKey;
  return `s:${contentHash(foldKey)}:${contentHash(`${foldKey.length}:${foldKey}`)}`;
}

/** Where a question's first occurrence starts (`questionRowId`). */
export const QUESTION_FIRST_OCCURRENCE = "start";

/**
 * The row id of one occurrence of a question fold: the question key plus
 * where the occurrence starts, which is {@link QUESTION_FIRST_OCCURRENCE} or
 * the Done time of the occurrence before it. A Done on it settles the copies
 * stored up to the Done, and the copies stored later are the next occurrence,
 * under the id that Done time names (`pendingQuestions` in feedShaping). The
 * id depends on no copy, so a copy that expires or sits on another page never
 * moves it.
 */
export function questionRowId(askKey: string, start: string): string {
  return rowId(`${askKey}#${start}`, start);
}

function presentedSummary(item: PresentableNotification, isCompletion: boolean, outcome: FeedOutcome | null): string {
  const stored = typeof item.summary === "string" ? item.summary : "";
  if (!isCompletion) return stored;
  if (outcome === "cancelled") return CANCELLED_SUMMARY;
  // The stored summary is the turn's answer (or, for a failure, what went
  // wrong): its first line tells one result from the next.
  const line = answerLine(stored);
  return line && line !== COMPLETION_NOTIFICATION_FALLBACK_SUMMARY ? line : COMPLETION_SUMMARY;
}

/** A row title is at most this many characters. */
export const ANSWER_LINE_MAX = 140;

const HEADING = /^\s{0,3}#{1,6}\s+/;
const LINE_MARKER = /^\s{0,3}(?:#{1,6}\s+|>\s?|[-*+]\s+|\d{1,3}[.)]\s+)/;

/** One line of Markdown as plain text: no block marker, emphasis, code ticks or link targets. */
function plainLine(line: string): string {
  return normalizePlainTextForDisplay(
    line
      .replace(LINE_MARKER, "")
      .replace(/!?\[([^\]]*)\]\([^)]*\)/g, "$1")
      .replace(/(\*\*|__|~~|\*|_|`)(?=\S)(.+?)(?<=\S)\1/g, "$2")
      .replace(/`/g, ""),
  );
}

/**
 * The first line of an answer as a short plain title: Markdown removed, a
 * heading joined to the line under it ("Weather in San Jose: 54–81°F"), and
 * cut to {@link ANSWER_LINE_MAX} at a sentence end or a word. Empty when the
 * answer has no text. Pure and local: no model call.
 */
export function answerLine(answer: string): string {
  const raw = answer.split(/\r?\n/);
  const lines: Array<{ text: string; heading: boolean }> = [];
  let inFence = false;
  for (const line of raw) {
    if (/^\s{0,3}(?:```|~~~)/.test(line)) { inFence = !inFence; continue; }
    // Code and table rows are content, not a title.
    if (inFence || /^\s*\|/.test(line) || /^[\s|:-]+$/.test(line)) continue;
    const text = plainLine(line);
    if (text) lines.push({ text, heading: HEADING.test(line) });
    if (lines.length === 2) break;
  }
  if (lines.length === 0) return "";
  const [first, next] = lines;
  const joined = first.heading && next
    ? `${first.text.replace(/[:\s]+$/, "")}: ${next.text}`
    : first.text;
  return shortLine(joined);
}

function shortLine(value: string): string {
  if (value.length <= ANSWER_LINE_MAX) return value;
  const head = value.slice(0, ANSWER_LINE_MAX);
  const sentence = leadingSentences(value, 40);
  if (sentence && sentence.length <= ANSWER_LINE_MAX && /[.!?]["')\]\u201d\u2019]*$/.test(sentence)) return sentence;
  const space = head.lastIndexOf(" ", ANSWER_LINE_MAX - 1);
  return `${(space > 40 ? head.slice(0, space) : head.slice(0, ANSWER_LINE_MAX - 1)).replace(/[\s,;:–—-]+$/, "")}…`;
}

const minOf = (...values: Array<number | null>) => {
  const present = values.filter((value): value is number => value !== null);
  return present.length ? Math.min(...present) : null;
};

/**
 * A completion the user has already seen whose answer is the whole result:
 * a receipt ("Done. Your reminder is set"). It never surfaces in the feed.
 */
export function isSeenCompletionReceipt(item: PresentableNotification, completion: CompletionFacts | null): boolean {
  const handoff = completionNotification(item)?.handoff;
  if (!handoff || !completion) return false;
  return completion.outcome === "succeeded" && completion.componentCount === 0
    && (handoff.status === "delivered" || handoff.status === "opened");
}

/** A completion whose hand-off the user dismissed or the system suppressed. */
export function isClosedCompletion(item: PresentableNotification): boolean {
  const status = completionNotification(item)?.handoff.status;
  return status === "dismissed" || status === "suppressed";
}

// ── Server: groups ──────────────────────────────────────────────────────────

/** At least this many rows of one kind on one day collapse into a group. */
export const FEED_GROUP_MIN = 2;

/** A group's label templates, one and many; each is a catalog source (`notificationMessages.ts`). */
export const GROUP_LABELS: Partial<Record<FeedKind, [one: string, many: string]>> = {
  task: ["1 task result", "{count} task results"],
  failed_task: ["1 failed task", "{count} failed tasks"],
  cancelled_task: ["1 cancelled task", "{count} cancelled tasks"],
  confirmed_meeting: ["1 confirmed meeting", "{count} confirmed meetings"],
  calendar: ["1 calendar update", "{count} calendar updates"],
  device: ["1 device update", "{count} device updates"],
  update: ["1 update", "{count} updates"],
};

const DEFAULT_GROUP_LABEL: [one: string, many: string] = ["1 update", "{count} updates"];

/** "3 task results": a group's label for its kind and member count, in the view's words (`t`). */
export function feedGroupLabel(kind: FeedKind, count: number, t: Translate = sourceEnglish): string {
  const [one, many] = GROUP_LABELS[kind] ?? DEFAULT_GROUP_LABEL;
  return count === 1 ? t(one) : t(many, { count });
}

/** Kinds that collapse into day groups once they can only sit in the background. */
export const GROUPABLE_KINDS: ReadonlySet<FeedKind> = new Set(Object.keys(GROUP_LABELS) as FeedKind[]);

/** True when, from `now` on, the row can only be in the background. */
export function isBackgroundOnly(presentation: FeedRowPresentation, now: number): boolean {
  return presentation.attention.every((window) =>
    (window.until !== null && window.until <= now) || (window.releasedByRead && presentation.state.read));
}

/** The local calendar day of `at` in `timeZone` (YYYY-MM-DD); UTC when the zone is unknown. */
export function localDayKey(at: number, timeZone: string): string {
  const format = (zone: string) => new Intl.DateTimeFormat("en-CA", { timeZone: zone, year: "numeric", month: "2-digit", day: "2-digit" }).format(at);
  try {
    return format(timeZone);
  } catch {
    return format("UTC");
  }
}

/** A valid IANA time zone name, or null. */
export function validTimeZone(value: unknown): string | null {
  if (typeof value !== "string" || !value || value.length > 64) return null;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value });
    return value;
  } catch {
    return null;
  }
}

/** The group of task results in NOW that the person has not seen yet. */
export const UNSEEN_RESULTS_GROUP_ID = "g:task:unseen";

/** The group a row joins at `now`, or null. */
function feedGroupKey(presentation: FeedRowPresentation, now: number, timeZone: string): string | null {
  if (!GROUPABLE_KINDS.has(presentation.kind)) return null;
  if (isBackgroundOnly(presentation, now)) return `g:${presentation.kind}:${localDayKey(presentation.when.timestamp, timeZone)}`;
  // Successful results waiting in NOW read as one row; a failure keeps its own.
  const unseenResult = presentation.kind === "task" && presentation.outcome === "succeeded"
    && feedRowSection(presentation, { now, read: presentation.state.read, done: presentation.state.done }) === "now";
  return unseenResult ? UNSEEN_RESULTS_GROUP_ID : null;
}

/**
 * Assigns groups, each needing {@link FEED_GROUP_MIN} members:
 * - Day groups: rows of one groupable kind and outcome, stored on one local
 *   day, that can only sit in the background from `now` on.
 * - The unseen results: successful task results that hold NOW at `now`
 *   ({@link UNSEEN_RESULTS_GROUP_ID}). A single one stays a row of its own.
 * Rows keep their order; members are newest first.
 */
export function groupFeedRows<T extends { presentation: FeedRowPresentation }>(rows: T[], { now, timeZone }: { now: number; timeZone: string }): T[] {
  const buckets = new Map<string, number[]>();
  rows.forEach((row, index) => {
    const key = feedGroupKey(row.presentation, now, timeZone);
    if (key) buckets.set(key, [...(buckets.get(key) ?? []), index]);
  });
  const result = [...rows];
  for (const [id, indexes] of buckets) {
    if (indexes.length < FEED_GROUP_MIN) continue;
    const members = indexes.map((index) => rows[index]).sort((a, b) => b.presentation.when.timestamp - a.presentation.when.timestamp);
    const group: FeedGroup = {
      id,
      label: feedGroupLabel(members[0].presentation.kind, members.length),
      count: members.length,
      memberRowIds: members.map((member) => member.presentation.id),
    };
    for (const index of indexes) result[index] = { ...rows[index], presentation: { ...rows[index].presentation, group } };
  }
  return result;
}

// ── Durable state ───────────────────────────────────────────────────────────

/**
 * - `read`: the row is read.
 * - `done`: the row is done (and so read); it leaves every feed page.
 * - `reopen`: takes back a Done. The row is back as it was before the Done:
 *   read only if it was read then. Only a row done at the sent revision
 *   changes; anything else is left as it is, so a repeat, or a reopen after
 *   the row changed, is a no-op.
 * - `undo`: forgets the row's state at any revision: unread and not done.
 *
 * Every op is idempotent. Ops apply in order within one request.
 */
export type FeedStateOpName = "read" | "done" | "reopen" | "undo";

/** One write: what to do to which rows, each at the revision the client saw. */
export type FeedStateOp = { op: FeedStateOpName; rows: Array<{ id: string; revision: string }> };

/** At most this many ops, and this many rows across them, per write. */
export const FEED_STATE_MAX_OPS = 20;
export const FEED_STATE_MAX_ROWS = 500;
/** Longest row id and revision a write may carry (UTF-16 code units). */
export const FEED_STATE_ROW_ID_MAX = 300;
export const FEED_STATE_REVISION_MAX = 64;

/** `{"id":"","revision":""},` plus room for a space after each separator. */
const ROW_JSON_BYTES = 32;
/** `{"op":"reopen","rows":[]},` plus spacing. */
const OP_JSON_BYTES = 32;
/** `{"ops":[]}` plus spacing. */
const BODY_JSON_BYTES = 64;
/**
 * The largest valid ops body in bytes: every row at the longest id and
 * revision. A code unit is at most 3 UTF-8 bytes (a surrogate pair is 4 for
 * two units), and JSON escapes `"` and `\` in 2; ids and revisions carry no
 * control characters, the only ones JSON widens to 6. So the request body
 * limit admits every write the row caps admit.
 */
export const FEED_STATE_MAX_REQUEST_BYTES =
  FEED_STATE_MAX_ROWS * (3 * (FEED_STATE_ROW_ID_MAX + FEED_STATE_REVISION_MAX) + ROW_JSON_BYTES)
  + FEED_STATE_MAX_OPS * OP_JSON_BYTES
  + BODY_JSON_BYTES;

/** Stored per row id: when it was read and done, at which revision. */
export type FeedStateEntry = { r?: number; d?: number; v: string; at: number };

export type FeedStateEntries = Record<string, FeedStateEntry>;

/** The state of a row at its current revision. */
export function rowState(entries: FeedStateEntries, id: string, revision: string): FeedRowState {
  const entry = Object.prototype.hasOwnProperty.call(entries, id) ? entries[id] : undefined;
  if (!entry || entry.v !== revision) return { read: false, done: false };
  return { read: typeof entry.r === "number" || typeof entry.d === "number", done: typeof entry.d === "number" };
}

/**
 * How long an entry outlives its row: an entry whose row the feed no longer
 * shows (gone, over, past the 30-day lookback) is dropped this long after its
 * last write. An entry whose row still shows is kept however old it is.
 */
export const FEED_STATE_RETENTION_MS = 31 * 24 * 60 * 60 * 1000;
/** At most this many rows keep state. */
export const FEED_STATE_MAX_ENTRIES = 1000;
/** At most about this many bytes of entries, well inside one silo document. */
export const FEED_STATE_MAX_STORED_BYTES = 512 * 1024;
/**
 * A prune keeps at most this many entries and bytes (besides the rows it
 * writes), below the caps that trigger it: a state at the caps then takes
 * about a hundred new rows before the next prune, instead of one per tap.
 */
export const FEED_STATE_PRUNED_ENTRIES = 900;
export const FEED_STATE_PRUNED_STORED_BYTES = 448 * 1024;

/** An upper bound on an entry's stored size: 3 bytes per code unit plus its fields. */
const storedEntryBytes = (id: string, entry: FeedStateEntry) => 3 * (id.length + entry.v.length) + 64;

/**
 * True when stored entries hold one to prune: one written over the retention
 * ago, or more than the caps hold. Decided on the entries after a write.
 */
export function feedStateMayNeedPrune(entries: FeedStateEntries, now: number): boolean {
  const list = Object.entries(entries);
  if (list.length > FEED_STATE_MAX_ENTRIES) return true;
  let bytes = 0;
  for (const [id, entry] of list) {
    if (now - entry.at >= FEED_STATE_RETENTION_MS) return true;
    bytes += storedEntryBytes(id, entry);
  }
  return bytes > FEED_STATE_MAX_STORED_BYTES;
}

/**
 * Applies ops to the stored entries, then prunes. Pure.
 *
 * `live` is the set of row ids the feed shows right now, or null when it is
 * unknown. With it, an entry whose row is not live and that was last written
 * over FEED_STATE_RETENTION_MS ago is dropped; without it nothing is dropped
 * for age, so a long-lived row never comes back for want of a read. A live
 * entry past the retention is restamped `at: now`. The prune then keeps
 * live entries first, then the newest: down to FEED_STATE_PRUNED_ENTRIES and
 * FEED_STATE_PRUNED_STORED_BYTES with `live`, to the caps without it. An
 * entry written by these ops is never dropped, so the result holds at most
 * those or one request's rows.
 * `prune: false` applies the ops only (the caller prunes in a later write
 * once it knows the live rows; see `feedStateMayNeedPrune`).
 */
export function applyFeedStateOps(
  entries: FeedStateEntries,
  ops: readonly FeedStateOp[],
  now: number,
  live: ReadonlySet<string> | null = null,
  prune = true,
): FeedStateEntries {
  const next: FeedStateEntries = { ...entries };
  const written = new Set<string>();
  for (const { op, rows } of ops) {
    for (const { id, revision } of rows) {
      const own = Object.prototype.hasOwnProperty.call(next, id) ? next[id] : undefined;
      const current = own && own.v === revision ? own : undefined;
      if (op === "undo") {
        delete next[id];
        written.delete(id);
        continue;
      }
      if (op === "reopen") {
        if (!current || typeof current.d !== "number") continue;
        // A Done does not write `r` (it reads as read through `d`), so `r`
        // here is a read from before the Done.
        if (typeof current.r === "number") {
          next[id] = { v: current.v, r: current.r, at: now };
          written.add(id);
        } else {
          delete next[id];
          written.delete(id);
        }
        continue;
      }
      written.add(id);
      if (op === "read") {
        next[id] = { ...(current ?? {}), v: revision, r: current?.r ?? now, at: now };
      } else {
        next[id] = { ...(current ?? {}), v: revision, d: now, at: now };
      }
    }
  }
  if (!prune) return next;
  const rank = (id: string) => (written.has(id) ? 0 : live?.has(id) ? 1 : 2);
  const candidates = Object.entries(next)
    .filter(([id, entry]) => live === null || rank(id) < 2 || now - entry.at < FEED_STATE_RETENTION_MS)
    // A live entry past the retention is seen live now: restamping it keeps
    // the next writes from paying for another live-row read until it ages
    // out again.
    .map(([id, entry]): [string, FeedStateEntry] =>
      rank(id) === 1 && now - entry.at >= FEED_STATE_RETENTION_MS ? [id, { ...entry, at: now }] : [id, entry])
    .sort(([leftId, left], [rightId, right]) => rank(leftId) - rank(rightId) || right.at - left.at);
  // Unknown live rows: only the caps, since any entry dropped might be a
  // live row's and bring it back.
  const maxEntries = live === null ? FEED_STATE_MAX_ENTRIES : FEED_STATE_PRUNED_ENTRIES;
  const maxBytes = live === null ? FEED_STATE_MAX_STORED_BYTES : FEED_STATE_PRUNED_STORED_BYTES;
  const kept: Array<[string, FeedStateEntry]> = [];
  let bytes = 0;
  for (const [id, entry] of candidates) {
    const size = storedEntryBytes(id, entry);
    if (rank(id) > 0 && (kept.length >= maxEntries || bytes + size > maxBytes)) continue;
    kept.push([id, entry]);
    bytes += size;
  }
  return Object.fromEntries(kept);
}
